import React from 'react';
import {
  CalendarMonth as CalendarIcon,
  Groups as GroupsIcon,
  Straighten as DistanceIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Contest, Team } from '../../models/types';
import { StatTile, StatGrid } from '../ui/StatTile';
import { formatKm, formatShortDate } from './contestFormat';
import { StandingEntry } from './useContestStandings';

interface ContestMetaTilesProps {
  contest: Contest;
  teams: Team[];
  currentUserId: string | null;
  standings: StandingEntry[];
  standingsLoading: boolean;
}

// Compact facts under the header; totals and ranks come from live standings, not stored team fields.
export const ContestMetaTiles = ({ contest, teams, currentUserId, standings, standingsLoading }: ContestMetaTilesProps) => {
  const { t } = useTranslation('contests');
  const isTeam = contest.contestType === 'Team';
  const totalDistance = standings.reduce((sum, s) => sum + s.totalDistance, 0);

  // Team contests rank the viewer's team; individual contests rank the viewer.
  const myStandingId = isTeam ? teams.find(team => team.members?.some(m => m.userId === currentUserId))?._id : currentUserId;
  const mine = currentUserId ? standings.find(s => s.id === myStandingId) : undefined;
  const mineValue = mine && (isTeam ? `${mine.name} · #${mine.rank}` : `#${mine.rank} · ${formatKm(mine.totalDistance)}`);

  return (
    <StatGrid>
      <StatTile variant="compact" icon={<CalendarIcon />} label={t('metaTiles.dates')} value={`${formatShortDate(contest.startAt)} – ${formatShortDate(contest.endAt)}`} />
      <StatTile
        variant="compact"
        icon={<GroupsIcon />}
        label={t('metaTiles.participants')}
        value={isTeam ? `${contest.numberOfParticipants} · ${t('listRow.teams', { count: contest.numberOfTeams ?? 0 })}` : contest.numberOfParticipants}
      />
      <StatTile variant="compact" icon={<DistanceIcon />} label={t('metaTiles.totalDistance')} value={standingsLoading ? '...' : formatKm(totalDistance)} />
      {mineValue && <StatTile variant="compact" icon={<TrophyIcon />} label={isTeam ? t('metaTiles.yourTeam') : t('metaTiles.yourRank')} value={mineValue} />}
    </StatGrid>
  );
};
